import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { useToast } from '@/components/ui/hooks/use-toast';
import { LoaderIcon } from '@/components/ui/loader-icon';
import { comboBoxOptionsOf, emptySelectOption, selectOption } from '@/components/ui/multi-select-utils';
import { useClassTimeOptions } from '@/lib/api-call-hooks';
import { bulkUpdateStudentProfiles } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { StudentOfSchool } from '@airreading/common/data';
import { useMutation } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { ReactNode } from 'react';
import { z } from 'zod';

interface BulkAssignClassTimesProps {
  students: StudentOfSchool[];
  trigger: ReactNode;
  schoolId: string;
  onSuccess?: () => void;
  schoolTimeZone: string;
}

const formSchema = z.object({
  classTime: selectOption,
});

export const BulkAssignClassTimes = ({ students, trigger, schoolId, onSuccess }: BulkAssignClassTimesProps) => {
  const { toast } = useToast();
  const { classTimeOptions, isPending, error } = useClassTimeOptions({ schoolId });
  const { mutateAsync } = useMutation({
    mutationFn: bulkUpdateStudentProfiles,
  });
  const onSubmit = ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    const { classTime } = formData;
    return mutateAsync({
      studentIds: students.map(({ id }) => id),
      options: {
        requestedClassTimeId: classTime.value,
      },
    })
      .then(() => {
        toast({
          title: `Assign class times for ${students.length} student(s)`,
        });
        onSuccess?.();
        closeModal();
      })
      .catch((e) => {
        toast({
          title: 'Error updating class times',
          description: airServerErrorFrom(e)?.message,
          variant: 'destructive',
        });
      });
  };

  return (
    <FormModal
      trigger={trigger}
      modalTitle={`Assign class times`}
      formSchema={formSchema}
      onSubmit={onSubmit}
      defaultValues={{
        classTime: emptySelectOption,
      }}
    >
      {({ form }) => {
        if (isPending) {
          return <LoaderIcon />;
        }
        if (error) {
          return (
            <div className="flex items-center gap-2">
              <Info className="text-red-500" />
              <span>{airServerErrorFrom(error)?.message}</span>
            </div>
          );
        }
        if (!classTimeOptions.length) {
          return <span>No schedules/class times available for this school. Please create class times first.</span>;
        }

        return (
          <div className="flex flex-col gap-2">
            <p>This will assign the same class time to all selected students.</p>
            <FormComboBox
              name="classTime"
              label="Class time (CT)"
              placeholder="Select class time"
              form={form}
              {...comboBoxOptionsOf(classTimeOptions)}
            />
          </div>
        );
      }}
    </FormModal>
  );
};
